import React, { useState, useEffect, useCallback } from 'react';
import { api, formatApiError } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Upload, DownloadSimple, FileText, Trash, PencilSimple, CalendarBlank } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ScheduleReminderDialog } from './ScheduleReminderDialog';

export const ClientAllDocsTab = ({ clientId }) => {
  const [sharedDocs, setSharedDocs] = useState([]);
  const [otherDocs, setOtherDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingShared, setUploadingShared] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editName, setEditName] = useState('');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDoc, setScheduleDoc] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/all-uploads`);
      setSharedDocs(data.shared_documents || []);
      setOtherDocs(data.other_documents || []);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleShareDocument = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingShared(true);
    const formData = new FormData(); formData.append('file', file);
    try {
      await api.post(`/clients/${clientId}/shared-documents?document_name=${encodeURIComponent(file.name)}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document shared with client'); fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setUploadingShared(false); e.target.value = ''; }
  };

  const handleDeleteDoc = async (docId, collection) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/shared-documents/${docId}`);
      toast.success('Document deleted'); fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const handleRenameDoc = async () => {
    if (!editingDoc || !editName.trim()) return;
    try {
      await api.put(`/shared-documents/${editingDoc.id}`, { document_name: editName.trim() });
      toast.success('Document renamed'); setEditingDoc(null); fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const handleDownload = async (doc) => {
    try {
      const path = doc.file_path || doc.response_file_path;
      const response = await api.get(`/files/${path}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = doc.original_filename || doc.document_name || 'download';
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download'); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const allDocs = [
    ...sharedDocs.map(d => ({ ...d, _source: 'shared' })),
    ...otherDocs.map(d => ({ ...d, _source: 'other', uploaded_by: 'client' })),
  ].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

  return (
    <>
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="heading-4 flex items-center gap-2"><FileText className="w-5 h-5" /> Documents <Badge variant="outline" className="ml-2">{allDocs.length}</Badge></CardTitle>
          <CardDescription>Shared documents and client uploads in one place</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <input type="file" id="share-doc-upload" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png" onChange={handleShareDocument} className="hidden" disabled={uploadingShared} data-testid="share-doc-input" />
            <Button variant="outline" onClick={() => document.getElementById('share-doc-upload').click()} disabled={uploadingShared} data-testid="share-doc-btn">
              <Upload className="w-4 h-4 mr-2" /> {uploadingShared ? 'Uploading...' : 'Share Document with Client'}
            </Button>
          </div>

          {allDocs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No documents yet</p>
          ) : (
            <div className="divide-y border rounded-md">
              {allDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between px-4 py-3" data-testid={`doc-row-${doc.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{doc.document_name || doc.original_filename || 'Document'}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {doc.created_at && <span className="text-xs text-muted-foreground">{new Date(doc.created_at).toLocaleDateString()}</span>}
                      {doc.uploaded_by === 'client' && <Badge variant="secondary" className="text-[10px] py-0 bg-blue-50 text-blue-600">Client Upload</Badge>}
                      {doc._source === 'shared' && doc.uploaded_by !== 'client' && <Badge variant="secondary" className="text-[10px] py-0">Shared by Admin</Badge>}
                      {doc.status === 'completed' && <Badge className="text-[10px] py-0 bg-green-50 text-green-600">Response Received</Badge>}
                      {doc.status === 'sent' && <Badge className="text-[10px] py-0 bg-amber-50 text-amber-600">Awaiting Response</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {doc.response_file_path && (
                      <Button variant="outline" size="sm" onClick={() => handleDownload({ ...doc, file_path: doc.response_file_path })} data-testid={`download-response-${doc.id}`}>
                        <DownloadSimple className="w-3.5 h-3.5 mr-1" /> Response
                      </Button>
                    )}
                    {doc.file_path && (
                      <Button variant="outline" size="sm" onClick={() => handleDownload(doc)} data-testid={`download-doc-${doc.id}`}>
                        <DownloadSimple className="w-3.5 h-3.5 mr-1" /> Download
                      </Button>
                    )}
                    {doc._source === 'shared' && (
                      <>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingDoc(doc); setEditName(doc.document_name); }} data-testid={`edit-doc-${doc.id}`}><PencilSimple className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setScheduleDoc(doc); setScheduleOpen(true); }} data-testid={`schedule-doc-${doc.id}`}><CalendarBlank className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteDoc(doc.id)} data-testid={`delete-doc-${doc.id}`}><Trash className="w-4 h-4 text-destructive" /></Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rename Dialog */}
      <Dialog open={!!editingDoc} onOpenChange={(open) => { if (!open) setEditingDoc(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Document</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <label className="text-sm font-medium">Document Name</label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="edit-doc-name-input" />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditingDoc(null)}>Cancel</Button>
              <Button onClick={handleRenameDoc} data-testid="save-doc-rename-btn">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      {scheduleDoc && (
        <ScheduleReminderDialog open={scheduleOpen} onOpenChange={setScheduleOpen} clientId={clientId} type="document" documentPath={scheduleDoc.file_path} documentName={scheduleDoc.document_name} onCreated={fetchData} />
      )}
    </>
  );
};
